'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Entity {
  id: string;
  name: string;
  code: string;
  region: string;
}

interface EntitySelectorProps {
  className?: string;
  onEntityChange?: (entities: Entity[]) => void;
}

const EntitySelector = ({ className = '', onEntityChange }: EntitySelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const entities: Entity[] = [
    { id: 'e-001', name: 'North America Operations', code: 'NAO', region: 'Americas' },
    { id: 'e-002', name: 'European Division', code: 'EUR', region: 'EMEA' },
    { id: 'e-003', name: 'Asia Pacific Group', code: 'APG', region: 'APAC' },
    { id: 'e-004', name: 'Latin America Unit', code: 'LAU', region: 'Americas' },
    { id: 'e-005', name: 'Middle East Operations', code: 'MEO', region: 'EMEA' },
    { id: 'e-006', name: 'UK Subsidiary', code: 'UKS', region: 'EMEA' },
    { id: 'e-007', name: 'Australia Branch', code: 'AUB', region: 'APAC' },
    { id: 'e-008', name: 'Canada Division', code: 'CAN', region: 'Americas' },
  ];

  useEffect(() => {
    const savedEntities = localStorage.getItem('selected-entities');
    if (savedEntities) {
      setSelectedEntities(JSON.parse(savedEntities));
    } else {
      const defaultEntity = [entities[0]];
      setSelectedEntities(defaultEntity);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      searchInputRef.current?.focus();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleEntityToggle = (entity: Entity) => {
    const isSelected = selectedEntities.some((e) => e.id === entity.id);
    let newSelection: Entity[];

    if (isSelected) {
      newSelection = selectedEntities.filter((e) => e.id !== entity.id);
    } else {
      newSelection = [...selectedEntities, entity];
    }

    setSelectedEntities(newSelection);
    localStorage.setItem('selected-entities', JSON.stringify(newSelection));
    onEntityChange?.(newSelection);
  };

  const handleSelectAll = () => {
    const filtered = getFilteredEntities();
    const allSelected = filtered.every((entity) => selectedEntities.some((e) => e.id === entity.id));

    let newSelection: Entity[];
    if (allSelected) {
      newSelection = selectedEntities.filter(
        (selected) => !filtered.some((filtered) => filtered.id === selected.id)
      );
    } else {
      const toAdd = filtered.filter((entity) => !selectedEntities.some((e) => e.id === entity.id));
      newSelection = [...selectedEntities, ...toAdd];
    }

    setSelectedEntities(newSelection);
    localStorage.setItem('selected-entities', JSON.stringify(newSelection));
    onEntityChange?.(newSelection);
  };

  const getFilteredEntities = () => {
    if (!searchQuery.trim()) return entities;
    const query = searchQuery.toLowerCase();
    return entities.filter(
      (entity) =>
        entity.name.toLowerCase().includes(query) ||
        entity.code.toLowerCase().includes(query) ||
        entity.region.toLowerCase().includes(query)
    );
  };

  const filteredEntities = getFilteredEntities();
  const allFilteredSelected = filteredEntities.every((entity) =>
    selectedEntities.some((e) => e.id === entity.id)
  );

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground border border-border rounded-md hover:bg-muted hover:shadow-md transition-all duration-250 ease-out active:scale-98 min-w-[200px] sm:min-w-[240px]"
        aria-label="Select entities"
        aria-expanded={isOpen}
      >
        <Icon name="BuildingOfficeIcon" size={20} className="text-text-secondary flex-shrink-0" />
        <span className="font-mono text-sm font-medium flex-1 text-left truncate">
          {selectedEntities.length === 0
            ? 'Select Entities'
            : selectedEntities.length === 1
            ? selectedEntities[0].name
            : `${selectedEntities.length} Entities`}
        </span>
        <Icon
          name={isOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
          size={16}
          className="text-text-secondary flex-shrink-0"
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full sm:w-96 bg-card border border-border rounded-md shadow-xl z-dropdown overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary space-y-3">
            <div className="relative">
              <Icon
                name="MagnifyingGlassIcon"
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search entities..."
                className="w-full pl-10 pr-4 py-2 bg-input text-foreground border border-border rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
              />
            </div>
            <button
              onClick={handleSelectAll}
              className="w-full px-3 py-2 bg-primary text-foreground border border-border rounded-md font-mono text-xs font-medium hover:bg-muted transition-all duration-250 ease-out"
            >
              {allFilteredSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {filteredEntities.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Icon name="MagnifyingGlassIcon" size={32} className="mx-auto text-text-secondary mb-2" />
                <p className="font-sans text-sm text-text-secondary">No entities found</p>
              </div>
            ) : (
              filteredEntities.map((entity) => {
                const isSelected = selectedEntities.some((e) => e.id === entity.id);
                return (
                  <button
                    key={entity.id}
                    onClick={() => handleEntityToggle(entity)}
                    className={`w-full px-4 py-3 text-left transition-all duration-250 ease-out flex items-center gap-3 ${
                      isSelected ? 'bg-accent/10' : 'hover:bg-secondary'
                    }`}
                  >
                    <div
                      className={`flex items-center justify-center w-5 h-5 border-2 rounded transition-all duration-250 ${
                        isSelected
                          ? 'bg-accent border-accent' :'bg-transparent border-border hover:border-accent'
                      }`}
                    >
                      {isSelected && <Icon name="CheckIcon" size={14} className="text-accent-foreground" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium text-foreground truncate">
                          {entity.name}
                        </span>
                        <span className="font-data text-xs text-text-secondary flex-shrink-0">
                          {entity.code}
                        </span>
                      </div>
                      <span className="font-sans text-xs text-text-secondary">{entity.region}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          {selectedEntities.length > 0 && (
            <div className="px-4 py-3 border-t border-border bg-secondary">
              <div className="flex items-center justify-between">
                <span className="font-sans text-sm text-text-secondary">
                  {selectedEntities.length} selected
                </span>
                <button
                  onClick={() => {
                    setSelectedEntities([]);
                    localStorage.removeItem('selected-entities');
                    onEntityChange?.([]);
                  }}
                  className="px-3 py-1.5 bg-error/10 text-error border border-error/20 rounded-md font-mono text-xs font-medium hover:bg-error/20 transition-all duration-250 ease-out"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EntitySelector;